import React, { useState, useEffect, useRef } from "react";
import CopyCatLogin from "./CopyCatLogin";
import CopyCatChat from "./CopyCatChat";
import { useAudioRecorder } from "./hooks/useAudioRecorder";
import { transcribeAudio } from "./services/whisper";
import { appendToContext } from "./services/contextBuffer";
import { fillInspectionForm, analyzePhoto, summarizeChunk, detectBoundingBox, speakFinding } from "./services/llmFill";
import { saveInspection, loadMachineHistory } from "./services/supermemory";
import { CAT_WORDBANK, matchWordbank, matchSmells, classifyMachine, MACHINE_TYPES, SMELL_SEVERITY } from "./services/CAT_WORDBANK";

// ── MACHINE FLEET ─────────────────────────────────────────────────────────
// Asset IDs, serial numbers, and vehicle classifications from CAT inspection PDFs
// Excavator series: 300.9-308 (Mini), 312-318 (GC), 320-352 (HC), 365-5230 (QM)
const MACHINES = [
  {
    id: "FL-3062",
    assetId: "CAT-982-3062",
    model: "CAT 982",
    fullName: "CAT 982 Large Wheel Loader",
    type: "Wheel Loader",
    machineClass: "wheel loader",
    category: "LOADER",
    categoryIcon: "🚜",
    year: "2019",
    serialNo: "W8R03062",
    inspectionForm: "982-WL",
    smuHours: 4820,
    operatorName: "J. Martinez",
  },
  {
    id: "EX-3201",
    assetId: "CAT-320-3201",
    model: "CAT 320",
    fullName: "CAT 320 Hydraulic Excavator",
    type: "Hydraulic Excavator",
    machineClass: "excavator",
    category: "EXCAVATOR",
    categoryIcon: "⛏️",
    year: "2020",
    serialNo: "MXB03201",
    inspectionForm: "320-352-HEX",
    smuHours: 3140,
    operatorName: "T. Brooks",
  },
  {
    id: "EX-3089",
    assetId: "CAT-308-3089",
    model: "CAT 308",
    fullName: "CAT 308 Mini Excavator",
    type: "Mini Excavator",
    machineClass: "excavator",
    category: "EXCAVATOR",
    categoryIcon: "⛏️",
    year: "2022",
    serialNo: "H6D03089",
    inspectionForm: "300.9-308-HEX",
    smuHours: 890,
    operatorName: "R. Chen",
  },
  {
    id: "FL-2991",
    assetId: "CAT-966-2991",
    model: "CAT 966",
    fullName: "CAT 966 Medium Wheel Loader",
    type: "Wheel Loader",
    machineClass: "wheel loader",
    category: "LOADER",
    categoryIcon: "🚜",
    year: "2018",
    serialNo: "P9N02991",
    inspectionForm: "966-WL",
    smuHours: 7310,
    operatorName: "D. Williams",
  },
];

// Excavator-specific inspection fields from the CAT HEX PDFs
const HEX_FIELDS = [
  // FROM THE GROUND
  { id: "G.1", section: "GROUND", label: "Bucket, GET", lookFor: "Excessive wear, damage, cracks", status: null, comment: "" },
  { id: "G.2", section: "GROUND", label: "Bucket Cylinder & Linkage", lookFor: "Wear, damage, leaks, lubricate", status: null, comment: "" },
  { id: "G.3", section: "GROUND", label: "Stick & Cylinder", lookFor: "Wear, damage, leaks, lubricate", status: null, comment: "" },
  { id: "G.4", section: "GROUND", label: "Boom & Cylinders", lookFor: "Wear, damage, leaks, lubricate", status: null, comment: "" },
  { id: "G.5", section: "GROUND", label: "Undercarriage", lookFor: "Wear, damage, tension", status: null, comment: "" },
  { id: "G.6", section: "GROUND", label: "Carbody", lookFor: "Cracks, damage", status: null, comment: "" },
  { id: "G.7", section: "GROUND", label: "Final Drive / Swing Drive", lookFor: "Leaks, damage", status: null, comment: "" },
  { id: "G.8", section: "GROUND", label: "Steps & Handholds", lookFor: "Condition & cleanliness", status: null, comment: "" },
  // ENGINE / LOWER PLATFORM
  { id: "E.1", section: "ENGINE", label: "Engine Coolant", lookFor: "Fluid level", status: null, comment: "" },
  { id: "E.2", section: "ENGINE", label: "Engine Oil", lookFor: "Fluid level", status: null, comment: "" },
  { id: "E.3", section: "ENGINE", label: "Hydraulic Oil Tank", lookFor: "Fluid level, damage, leaks", status: null, comment: "" },
  { id: "E.4", section: "ENGINE", label: "Radiator", lookFor: "Fin blockage, leaks", status: null, comment: "" },
  { id: "E.5", section: "ENGINE", label: "All Hoses", lookFor: "Cracks, wear spots, leaks", status: null, comment: "" },
  { id: "E.6", section: "ENGINE", label: "All Belts", lookFor: "Tightness, wear, cracks", status: null, comment: "" },
  { id: "E.7", section: "ENGINE", label: "Air Filter", lookFor: "Restriction indicator", status: null, comment: "" },
  { id: "E.8", section: "ENGINE", label: "Fuel Tank", lookFor: "Fuel level, damage, leaks", status: null, comment: "" },
  { id: "E.9", section: "ENGINE", label: "DEF Tank", lookFor: "Fluid level, debris buildup", status: null, comment: "" },
  // INSIDE CAB
  { id: "C.1", section: "CAB", label: "Seat & Seatbelt", lookFor: "Adjustment, wear, install date (<3yr)", status: null, comment: "" },
  { id: "C.2", section: "CAB", label: "Horn, Travel Alarm, Lights", lookFor: "Proper function", status: null, comment: "" },
  { id: "C.3", section: "CAB", label: "Travel Controls", lookFor: "Correct operation, free from debris", status: null, comment: "" },
  { id: "C.4", section: "CAB", label: "ROPS", lookFor: "Damage, loose mounting bolts", status: null, comment: "" },
  { id: "C.5", section: "CAB", label: "Heating & Cooling System", lookFor: "Proper function", status: null, comment: "" },
];

// Wheel loader fields (original)
const WL_FIELDS = [
  { id: "1.1", section: "GROUND", label: "Tires and Rims", lookFor: "Wear, damage, pressure", status: null, comment: "" },
  { id: "1.2", section: "GROUND", label: "Bucket Cutting Edge", lookFor: "Wear, damage, cracks", status: null, comment: "" },
  { id: "1.3", section: "GROUND", label: "Bucket Cylinders & Hoses", lookFor: "Leaks, wear, damage", status: null, comment: "" },
  { id: "1.7", section: "GROUND", label: "Transmission & Gears", lookFor: "Oil level, leaks, noise", status: null, comment: "" },
  { id: "1.8", section: "GROUND", label: "Differential & Drive Oil", lookFor: "Level, leaks", status: null, comment: "" },
  { id: "2.1", section: "ENGINE", label: "Engine Oil Level", lookFor: "Fluid level", status: null, comment: "" },
  { id: "2.2", section: "ENGINE", label: "Engine Coolant", lookFor: "Fluid level, leaks", status: null, comment: "" },
  { id: "2.3", section: "ENGINE", label: "Radiator Cores", lookFor: "Fin blockage, debris", status: null, comment: "" },
  { id: "4.1", section: "CAB", label: "Seat & Seatbelt", lookFor: "Adjustment, wear, install date", status: null, comment: "" },
  { id: "4.4", section: "CAB", label: "Backup Alarm", lookFor: "Proper function", status: null, comment: "" },
];

function getFieldsForMachine(machine) {
  return machine.machineClass === "excavator" ? HEX_FIELDS.map(f => ({ ...f })) : WL_FIELDS.map(f => ({ ...f }));
}

const DEFAULT_FIELDS = WL_FIELDS.map(f => ({ ...f }));

const DOT = { PASS: "#22c55e", MONITOR: "#eab308", FAIL: "#ef4444", null: "#1e2530" };
const LABEL = {
  PASS: { color: "#22c55e", bg: "#0a2016" },
  MONITOR: { color: "#eab308", bg: "#1f1500" },
  FAIL: { color: "#ef4444", bg: "#200a0a" },
};

const CAT_TERMS = CAT_WORDBANK;
const CAT_COLORS = {
  TEL:   { bg: "#0a1628", border: "#1d4ed8", text: "#60a5fa" },
  SOS:   { bg: "#1a0a28", border: "#7c3aed", text: "#a78bfa" },
  PWR:   { bg: "#1f1500", border: "#d97706", text: "#fbbf24" },
  UND:   { bg: "#0a2016", border: "#15803d", text: "#4ade80" },
  PERF:  { bg: "#1a1000", border: "#b45309", text: "#f59e0b" },
  MNT:   { bg: "#200a0a", border: "#b91c1c", text: "#f87171" },
  SMELL: { bg: "#1a0808", border: "#dc2626", text: "#fca5a5" },
};

// Category colors for machine classification
const CAT_CLASS_COLORS = {
  LOADER:   { bg: "#0d1117", border: "#FFCD11", text: "#FFCD11" },
  EXCAVATOR:{ bg: "#0d1a1a", border: "#22d3ee", text: "#22d3ee" },
  TRUCK:    { bg: "#0d1a0d", border: "#22c55e", text: "#22c55e" },
  DOZER:    { bg: "#1a0d0d", border: "#f97316", text: "#f97316" },
};

function fmt(s) { return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; }

function extractCATTerms(text) { return matchWordbank(text).slice(0, 6); }

function detectMachineFromText(text) {
  if (!text) return null;
  const upper = text.toUpperCase();
  // Pass 1: exact unit ID match (e.g. "EX-3089" or "EX 3089") — most reliable
  for (const m of MACHINES) {
    if (upper.includes(m.id) || upper.includes(m.id.replace("-", " "))) return m;
  }
  // Pass 2: exact asset ID match
  for (const m of MACHINES) {
    if (upper.includes(m.assetId)) return m;
  }
  // Pass 3: full model name only — NOT partial number match to avoid EX-3089/EX-3201 collision
  for (const m of MACHINES) {
    const fullModel = m.fullName.toUpperCase();
    if (upper.includes(fullModel)) return m;
  }
  return null;
}

// Quick local summary (no API call) — used as fallback
function quickSummary(text) {
  if (!text) return null;
  const lower = text.toLowerCase();
  const isFail = /fail|broken|crack|leak|grind|worn|damage|missing|bad|wrong|problem|issue|noise|smell|burn|overheat|low|empty|stuck|seized|bent/.test(lower);
  const isPass = /good|fine|ok|okay|pass|clean|clear|normal|looks great|no issue|all good|solid|tight|works/.test(lower);
  const isMon = /monitor|watch|check|slight|minor|little|starting to|could be|maybe|getting/.test(lower);
  const components = [];
  if (/transmission|gear/.test(lower)) components.push("transmission");
  if (/coolant|cooling/.test(lower)) components.push("coolant system");
  if (/radiator/.test(lower)) components.push("radiator");
  if (/hydraulic/.test(lower)) components.push("hydraulics");
  if (/tire|tyre|rim/.test(lower)) components.push("tires/rims");
  if (/engine oil|oil level/.test(lower)) components.push("engine oil");
  if (/seat|seatbelt|belt/.test(lower)) components.push("cab seat");
  if (/alarm|backup/.test(lower)) components.push("backup alarm");
  if (/bucket|cutting edge|get/.test(lower)) components.push("bucket/GET");
  if (/differential|diff/.test(lower)) components.push("differential");
  if (/hose|seal|clamp/.test(lower)) components.push("hoses/seals");
  if (/boom|stick|undercarriage/.test(lower)) components.push("undercarriage");
  if (/swing|swing drive/.test(lower)) components.push("swing drive");
  if (/rops/.test(lower)) components.push("ROPS");
  const comp = components.length > 0 ? components.join(" + ") : "component";
  if (isFail) return `⚠ Issue on ${comp}`;
  if (isPass) return `✓ ${comp} looks good`;
  if (isMon) return `~ ${comp} needs monitoring`;
  if (components.length > 0) return `Inspector noted ${comp}`;
  return null;
}

// Generate a short AI summary of the entire transcript for Fleet tab
function generateTranscriptSummary(chunks, fields) {
  if (!chunks || chunks.length === 0) return null;
  const failFields = fields.filter(f => f.status === "FAIL").map(f => f.label);
  const monFields = fields.filter(f => f.status === "MONITOR").map(f => f.label);
  const passFields = fields.filter(f => f.status === "PASS").map(f => f.label);
  const parts = [];
  if (failFields.length > 0) parts.push(`FAIL: ${failFields.join(", ")}`);
  if (monFields.length > 0) parts.push(`MON: ${monFields.join(", ")}`);
  if (passFields.length > 0) parts.push(`PASS: ${passFields.join(", ")}`);
  return parts.length > 0 ? parts.join(" · ") : "No issues detected";
}

function sessionOutcome(chunks, fields) {
  if (chunks.length === 0) return null;
  const failCount = fields.filter(f => f.status === "FAIL").length;
  const monCount = fields.filter(f => f.status === "MONITOR").length;
  const passCount = fields.filter(f => f.status === "PASS").length;
  const total = failCount + monCount + passCount;
  if (total === 0) {
    const allText = chunks.map(c => c.text).join(" ").toLowerCase();
    const hasIssue = /fail|broken|crack|leak|grind|worn|damage|missing|bad|problem|noise/.test(allText);
    const hasGood = /good|fine|ok|okay|pass|clean|normal|no issue|solid/.test(allText);
    if (hasIssue) return { label: "ISSUES DETECTED", color: "#ef4444", bg: "#200a0a", icon: "✗" };
    if (hasGood) return { label: "INSPECTION CLEAR", color: "#22c55e", bg: "#0a2016", icon: "✓" };
    return { label: "INCOMPLETE — REVIEW NEEDED", color: "#9ca3af", bg: "#111827", icon: "?" };
  }
  if (failCount > 0) return { label: `${failCount} FAIL · ${monCount} MON · ${passCount} PASS`, color: "#ef4444", bg: "#200a0a", icon: "✗" };
  if (monCount > 0) return { label: `${monCount} MONITOR · ${passCount} PASS`, color: "#eab308", bg: "#1f1500", icon: "~" };
  return { label: `ALL ${passCount} ITEMS PASSED`, color: "#22c55e", bg: "#0a2016", icon: "✓" };
}

function parseMemory(mem) {
  const content = mem.content || "";
  const failMatch = content.match(/FAIL: (.+)/);
  const monMatch = content.match(/MONITOR: (.+)/);
  const summaryMatch = content.match(/SUMMARY: (.+)/);
  return {
    date: mem.date || "Unknown",
    fail: failMatch ? failMatch[1].trim() : "none",
    monitor: monMatch ? monMatch[1].trim() : "none",
    summary: summaryMatch ? summaryMatch[1].trim() : null,
    full: content,
    fields: mem.fields || [],
  };
}

// ── CONFIDENCE INTERVAL BAR ───────────────────────────────────────────────
function ConfidenceBar({ confidence, status }) {
  if (!confidence) return null;
  const color = status === "FAIL" ? "#ef4444" : status === "MONITOR" ? "#eab308" : "#22c55e";
  const segments = 20;
  const filled = Math.round((confidence / 100) * segments);
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
        <span style={{ fontSize: 7, color: "#6b7280", letterSpacing: 1 }}>CONFIDENCE</span>
        <span style={{ fontSize: 7, color, fontWeight: 700 }}>{confidence}%</span>
      </div>
      <div style={{ display: "flex", gap: 2 }}>
        {Array.from({ length: segments }).map((_, i) => (
          <div key={i} style={{
            flex: 1, height: 4, borderRadius: 1,
            background: i < filled ? color : "#1e2530",
            opacity: i < filled ? (0.4 + (i / segments) * 0.6) : 1,
            transition: "background 0.3s",
          }} />
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
        <span style={{ fontSize: 6, color: "#374151" }}>LOW</span>
        <span style={{ fontSize: 6, color: "#374151" }}>HIGH</span>
      </div>
    </div>
  );
}

// ── WORDBANK VIEWER ───────────────────────────────────────────────────────
// Full searchable wordbank viewer — accessible from any tab via floating button
function WordbankModal({ onClose }) {
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("ALL");
  const cats = ["ALL", "TEL", "SOS", "PWR", "UND", "PERF", "MNT", "SMELL"];
  const filtered = CAT_WORDBANK.filter(t =>
    (filterCat === "ALL" || t.cat === filterCat) &&
    (search === "" || t.term.includes(search.toLowerCase()) || t.short.includes(search.toUpperCase()) || t.def.toLowerCase().includes(search.toLowerCase()))
  );
  return (
    <div style={{ position: "absolute", inset: 0, background: "rgba(10,10,10,0.97)", zIndex: 999, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ padding: "12px 16px", background: "#0d0d0d", borderBottom: "1px solid #1e2530", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 11, color: "#FFCD11", fontWeight: 700, letterSpacing: 2 }}>CAT WORDBANK</div>
          <div style={{ fontSize: 7, color: "#4b5563", marginTop: 1 }}>{filtered.length} terms · {CAT_WORDBANK.length} total</div>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "1px solid #374151", color: "#9ca3af", padding: "4px 10px", borderRadius: 4, cursor: "pointer", fontSize: 9, fontFamily: "inherit" }}>✕ CLOSE</button>
      </div>
      {/* Search + filter */}
      <div style={{ padding: "8px 14px", background: "#0a0a0a", borderBottom: "1px solid #141414", flexShrink: 0 }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search terms..."
          style={{ width: "100%", background: "#141414", border: "1px solid #1e2530", borderRadius: 4, padding: "6px 10px", color: "#e5e7eb", fontSize: 9, fontFamily: "IBM Plex Mono", outline: "none", marginBottom: 8 }}
        />
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {cats.map(c => (
            <button key={c} onClick={() => setFilterCat(c)}
              style={{ padding: "2px 7px", borderRadius: 3, fontSize: 7, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", letterSpacing: 0.5, border: "1px solid", background: filterCat === c ? (CAT_COLORS[c]?.bg || "#FFCD1120") : "transparent", color: filterCat === c ? (CAT_COLORS[c]?.text || "#FFCD11") : "#4b5563", borderColor: filterCat === c ? (CAT_COLORS[c]?.border || "#FFCD11") : "#1e2530" }}>
              {c}
            </button>
          ))}
        </div>
      </div>
      {/* Terms list */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {filtered.map((t, i) => {
          const c = CAT_COLORS[t.cat] || CAT_COLORS.MNT;
          return (
            <div key={i} style={{ padding: "9px 14px", borderBottom: "1px solid #0d0d0d", display: "flex", gap: 10, alignItems: "flex-start" }}>
              <div style={{ flexShrink: 0, padding: "2px 5px", borderRadius: 2, background: c.bg, border: `1px solid ${c.border}`, minWidth: 60, textAlign: "center" }}>
                <div style={{ fontSize: 6, color: c.text, fontWeight: 700, letterSpacing: 0.5 }}>{t.cat}</div>
                <div style={{ fontSize: 7, color: c.text, fontWeight: 700, marginTop: 1 }}>{t.short}</div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 8, color: "#e5e7eb", fontWeight: 600, marginBottom: 2 }}>{t.term}</div>
                <div style={{ fontSize: 7, color: "#6b7280", lineHeight: 1.5 }}>{t.def}</div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div style={{ padding: 40, textAlign: "center", fontSize: 9, color: "#374151" }}>No terms found</div>
        )}
      </div>
    </div>
  );
}

// ── SMOKE / SMELL DETECTOR ────────────────────────────────────────────────
// Real-time smoke scoring via voice transcript keyword analysis
// For hardware integration: connect to TensorFlow.js smoke classifier
// or fire/gas sensor via Web Bluetooth / WebUSB API
const SMELL_OPTIONS = [
  { id: "burning",     label: "Burning",     icon: "🔥", status: "FAIL",    desc: "Electrical, clutch, or brake overheating", smokeScore: 85 },
  { id: "blue_smoke",  label: "Blue Smoke",  icon: "💨", status: "FAIL",    desc: "Oil burning — worn rings or valve seals", smokeScore: 95 },
  { id: "white_smoke", label: "White Smoke", icon: "🌫️", status: "FAIL",    desc: "Coolant in combustion — head gasket failure", smokeScore: 90 },
  { id: "black_smoke", label: "Black Smoke", icon: "🖤", status: "MONITOR", desc: "Rich mixture — injector fault or air restriction", smokeScore: 75 },
  { id: "diesel",      label: "Diesel/Fuel", icon: "⛽", status: "FAIL",    desc: "Fuel leak near hot surfaces — fire risk", smokeScore: 70 },
  { id: "sweet",       label: "Sweet",       icon: "🍬", status: "FAIL",    desc: "Coolant leak — ethylene glycol odor", smokeScore: 60 },
  { id: "oil",         label: "Oil Odor",    icon: "🛢️", status: "MONITOR", desc: "Oil on hot surfaces — leak risk", smokeScore: 65 },
  { id: "sulfur",      label: "Sulfur/Egg",  icon: "🥚", status: "MONITOR", desc: "Battery overcharging or exhaust issue", smokeScore: 55 },
  { id: "rubber",      label: "Rubber",      icon: "⚫", status: "MONITOR", desc: "Slipping belt or overheated tire", smokeScore: 60 },
  { id: "hot_metal",   label: "Hot Metal",   icon: "⚙️", status: "FAIL",    desc: "Bearing failure or brake drag", smokeScore: 80 },
];

// ── REAL-TIME SMOKE DETECTION from voice transcript ───────────────────────
// Scans incoming transcript for smoke/smell keywords and auto-triggers
function detectSmokeFromText(text) {
  const lower = (text || "").toLowerCase();
  const found = [];
  const smokeKeywords = {
    "smoke": "blue_smoke", "smoking": "blue_smoke", "blue smoke": "blue_smoke",
    "white smoke": "white_smoke", "black smoke": "black_smoke",
    "burning": "burning", "burn": "burning", "on fire": "burning",
    "fuel smell": "diesel", "diesel smell": "diesel", "gas smell": "diesel",
    "sweet smell": "sweet", "coolant smell": "sweet",
    "oil smell": "oil", "oil burning": "blue_smoke",
    "sulfur": "sulfur", "rotten egg": "sulfur",
    "rubber smell": "rubber", "rubber burning": "rubber",
    "hot metal": "hot_metal", "metal burning": "hot_metal",
  };
  for (const [keyword, smellId] of Object.entries(smokeKeywords)) {
    if (lower.includes(keyword) && !found.includes(smellId)) {
      found.push(smellId);
    }
  }
  return found.map(id => SMELL_OPTIONS.find(o => o.id === id)).filter(Boolean);
}

function SmellPanel({ onSmellDetected, smellLog, lastAutoDetect }) {
  const [active, setActive] = useState(new Set());
  const [intensity, setIntensity] = useState(50);
  const [submitted, setSubmitted] = useState(false);

  const toggle = (id) => {
    setActive(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = () => {
    if (active.size === 0) return;
    const selected = SMELL_OPTIONS.filter(o => active.has(o.id));
    onSmellDetected(selected, intensity);
    setSubmitted(true);
    setTimeout(() => { setSubmitted(false); setActive(new Set()); }, 2000);
  };

  // Composite smoke risk score based on active selections
  const smokeRisk = active.size > 0
    ? Math.min(100, Math.round(Array.from(active).reduce((sum, id) => {
        const opt = SMELL_OPTIONS.find(o => o.id === id);
        return sum + (opt?.smokeScore || 0);
      }, 0) / active.size * (intensity / 70)))
    : 0;
  const riskColor = smokeRisk > 70 ? "#ef4444" : smokeRisk > 40 ? "#eab308" : "#22c55e";

  return (
    <div style={{ flex: 1, overflowY: "auto" }}>
      {/* Auto-detection banner */}
      {lastAutoDetect && (
        <div style={{ padding: "8px 14px", background: "#200a0a", borderBottom: "1px solid #7f1d1d", display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 10 }}>🔥</span>
          <div>
            <div style={{ fontSize: 7, color: "#ef4444", letterSpacing: 2, fontWeight: 700 }}>AUTO-DETECTED FROM VOICE</div>
            <div style={{ fontSize: 8, color: "#fca5a5" }}>{lastAutoDetect.map(s => s.label).join(", ")} — logged at {lastAutoDetect[0]?.time}</div>
          </div>
        </div>
      )}

      {/* Smoke risk meter */}
      {active.size > 0 && (
        <div style={{ padding: "10px 14px", background: "#0a0a0a", borderBottom: "1px solid #141414" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: 7, color: "#6b7280", letterSpacing: 2 }}>SMOKE / FIRE RISK SCORE</span>
            <span style={{ fontSize: 10, color: riskColor, fontWeight: 700 }}>{smokeRisk}%</span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: "#1e2530", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${smokeRisk}%`, background: riskColor, borderRadius: 3, transition: "width 0.4s, background 0.3s", boxShadow: `0 0 8px ${riskColor}80` }} />
          </div>
          <div style={{ fontSize: 7, color: riskColor, marginTop: 3 }}>
            {smokeRisk > 70 ? "⚠ HIGH RISK — immediate inspection required" : smokeRisk > 40 ? "~ MODERATE RISK — investigate source" : "✓ LOW RISK — monitor closely"}
          </div>
        </div>
      )}

      <div style={{ padding: "10px 14px", borderBottom: "1px solid #141414" }}>
        <div style={{ fontSize: 7, color: "#6b7280", letterSpacing: 2, marginBottom: 6 }}>SELECT DETECTED SMELLS / SMOKE</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          {SMELL_OPTIONS.map(opt => {
            const isActive = active.has(opt.id);
            const color = opt.status === "FAIL" ? "#ef4444" : "#eab308";
            return (
              <div key={opt.id} onClick={() => toggle(opt.id)}
                style={{ padding: "8px 10px", borderRadius: 6, cursor: "pointer", border: `1px solid ${isActive ? color : "#1e2530"}`, background: isActive ? `${color}15` : "#0d0d0d", transition: "all 0.2s" }}>
                <div style={{ fontSize: 16, marginBottom: 3 }}>{opt.icon}</div>
                <div style={{ fontSize: 8, color: isActive ? color : "#9ca3af", fontWeight: isActive ? 700 : 400, letterSpacing: 0.5 }}>{opt.label}</div>
                <div style={{ fontSize: 7, color: "#4b5563", marginTop: 2, lineHeight: 1.4 }}>{opt.desc}</div>
                <div style={{ fontSize: 6, color: "#374151", marginTop: 2 }}>smoke score: {opt.smokeScore}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Intensity slider */}
      <div style={{ padding: "12px 14px", borderBottom: "1px solid #141414" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
          <span style={{ fontSize: 7, color: "#6b7280", letterSpacing: 2 }}>INTENSITY</span>
          <span style={{ fontSize: 8, color: intensity > 70 ? "#ef4444" : intensity > 40 ? "#eab308" : "#22c55e", fontWeight: 700 }}>{intensity}%</span>
        </div>
        <input type="range" min="0" max="100" value={intensity} onChange={e => setIntensity(Number(e.target.value))}
          style={{ width: "100%", accentColor: intensity > 70 ? "#ef4444" : intensity > 40 ? "#eab308" : "#22c55e" }} />
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
          <span style={{ fontSize: 6, color: "#374151" }}>FAINT</span>
          <span style={{ fontSize: 6, color: "#374151" }}>STRONG</span>
        </div>
      </div>

      <div style={{ padding: "12px 14px", borderBottom: "1px solid #141414" }}>
        <button onClick={handleSubmit} disabled={active.size === 0}
          style={{ width: "100%", padding: "10px", borderRadius: 4, border: "none", cursor: active.size > 0 ? "pointer" : "not-allowed", background: submitted ? "#22c55e" : active.size > 0 ? "#ef4444" : "#1e2530", color: active.size > 0 ? "#fff" : "#374151", fontSize: 9, fontWeight: 700, letterSpacing: 2, fontFamily: "inherit", transition: "all 0.2s" }}>
          {submitted ? "LOGGED ✓" : `LOG ${active.size} SMELL${active.size !== 1 ? "S" : ""} →`}
        </button>
      </div>

      {smellLog.length > 0 && (
        <div style={{ padding: "8px 14px 4px", fontSize: 7, color: "#6b7280", letterSpacing: 2 }}>SMELL LOG</div>
      )}
      {smellLog.map((entry, i) => (
        <div key={i} style={{ padding: "8px 14px", borderBottom: "1px solid #0d0d0d" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: 7, color: "#6b7280" }}>{entry.time} {entry.autoDetected ? "· 🎙 AUTO" : ""}</span>
            <span style={{ fontSize: 7, color: "#ef4444" }}>INTENSITY {entry.intensity}%</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            {entry.smells.map((s, j) => (
              <span key={j} style={{ fontSize: 7, padding: "2px 5px", borderRadius: 2, background: s.status === "FAIL" ? "#200a0a" : "#1f1500", color: s.status === "FAIL" ? "#f87171" : "#fbbf24", border: `1px solid ${s.status === "FAIL" ? "#7f1d1d" : "#92400e"}` }}>
                {s.icon} {s.label}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── SVG ILLUSTRATIONS ─────────────────────────────────────────────────────
function WheelLoaderSVG({ size = 120 }) {
  const c = "#FFCD11";
  return (
    <svg width={size} height={size * 0.7} viewBox="0 0 200 140" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="55" y="40" width="90" height="55" rx="6" fill={c} opacity="0.9"/>
      <rect x="100" y="20" width="50" height="35" rx="4" fill={c}/>
      <rect x="106" y="25" width="38" height="22" rx="3" fill="#0a1628" opacity="0.8"/>
      <rect x="20" y="35" width="45" height="10" rx="3" fill={c} transform="rotate(-15 20 40)"/>
      <path d="M8 70 L35 55 L42 75 L15 88 Z" fill={c} opacity="0.85"/>
      <path d="M8 88 L42 75 L42 82 L8 95 Z" fill={c} opacity="0.6"/>
      <rect x="130" y="82" width="25" height="12" rx="3" fill={c} opacity="0.7"/>
      <circle cx="65" cy="105" r="24" fill="#1a1a1a" stroke={c} strokeWidth="4"/>
      <circle cx="65" cy="105" r="14" fill="#141414" stroke={c} strokeWidth="2" opacity="0.6"/>
      <circle cx="65" cy="105" r="4" fill={c}/>
      <circle cx="148" cy="105" r="20" fill="#1a1a1a" stroke={c} strokeWidth="4"/>
      <circle cx="148" cy="105" r="11" fill="#141414" stroke={c} strokeWidth="2" opacity="0.6"/>
      <circle cx="148" cy="105" r="4" fill={c}/>
    </svg>
  );
}

function ExcavatorSVG({ size = 120 }) {
  const c = "#22d3ee";
  return (
    <svg width={size} height={size * 0.75} viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Body */}
      <rect x="40" y="50" width="120" height="60" rx="6" fill={c} opacity="0.85"/>
      {/* Cab */}
      <rect x="100" y="25" width="55" height="40" rx="5" fill={c}/>
      <rect x="106" y="30" width="42" height="28" rx="3" fill="#041a1a" opacity="0.9"/>
      {/* Boom */}
      <rect x="30" y="40" width="15" height="65" rx="4" fill={c} transform="rotate(-30 30 40)"/>
      {/* Stick */}
      <rect x="8" y="20" width="12" height="55" rx="3" fill={c} opacity="0.85" transform="rotate(20 8 20)"/>
      {/* Bucket */}
      <path d="M2 85 L20 78 L25 95 L7 102 Z" fill={c} opacity="0.9"/>
      {/* Tracks */}
      <rect x="20" y="108" width="160" height="16" rx="8" fill="#1a1a1a" stroke={c} strokeWidth="3"/>
      <circle cx="30" cy="116" r="8" fill="#0a0a0a" stroke={c} strokeWidth="2"/>
      <circle cx="170" cy="116" r="8" fill="#0a0a0a" stroke={c} strokeWidth="2"/>
      <circle cx="100" cy="116" r="6" fill="#141414" stroke={c} strokeWidth="1.5" opacity="0.6"/>
    </svg>
  );
}

function MachineSVG({ machine, size = 120 }) {
  if (machine.machineClass === "excavator") return <ExcavatorSVG size={size} />;
  return <WheelLoaderSVG size={size} />;
}

// ── PHOTO BBOX OVERLAY ────────────────────────────────────────────────────
function PhotoBboxOverlay({ photoBbox }) {
  if (!photoBbox) return null;
  const statusColor = photoBbox.status === "FAIL" ? "#ef4444" : photoBbox.status === "MONITOR" ? "#eab308" : "#22c55e";
  return (
    <div style={{ margin: "10px 14px 0", borderRadius: 6, overflow: "visible", position: "relative", border: `1px solid ${statusColor}`, background: "#0d0d0d" }}>
      <div style={{ padding: "6px 10px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${statusColor}30` }}>
        <span style={{ fontSize: 7, color: "#6b7280", letterSpacing: 2 }}>PHOTO ANALYSIS</span>
        <span style={{ fontSize: 7, padding: "1px 5px", borderRadius: 2, background: LABEL[photoBbox.status]?.bg || "#111", color: statusColor, fontWeight: 700 }}>{photoBbox.status}</span>
      </div>
      <div style={{ position: "relative" }}>
        <img src={`data:image/jpeg;base64,${photoBbox.imageBase64}`} style={{ width: "100%", display: "block" }} alt="inspection" />
        {photoBbox.x !== undefined && (
          <>
            <div style={{ position: "absolute", left: `${photoBbox.x * 100}%`, top: `${photoBbox.y * 100}%`, width: `${photoBbox.w * 100}%`, height: `${photoBbox.h * 100}%`, border: `2px solid ${statusColor}`, boxShadow: `0 0 10px ${statusColor}60`, borderRadius: 3, pointerEvents: "none" }} />
            {[
              { top: `${photoBbox.y * 100}%`, left: `${photoBbox.x * 100}%` },
              { top: `${photoBbox.y * 100}%`, left: `${(photoBbox.x + photoBbox.w) * 100}%` },
              { top: `${(photoBbox.y + photoBbox.h) * 100}%`, left: `${photoBbox.x * 100}%` },
              { top: `${(photoBbox.y + photoBbox.h) * 100}%`, left: `${(photoBbox.x + photoBbox.w) * 100}%` },
            ].map((pos, i) => (
              <div key={i} style={{ position: "absolute", width: 8, height: 8, background: statusColor, borderRadius: 1, transform: "translate(-50%, -50%)", ...pos, pointerEvents: "none" }} />
            ))}
            <div style={{ position: "absolute", left: `${photoBbox.x * 100}%`, top: `calc(${photoBbox.y * 100}% - 22px)`, background: statusColor, color: "#000", fontSize: 7, fontWeight: 700, padding: "2px 6px", borderRadius: 2, fontFamily: "IBM Plex Mono", whiteSpace: "nowrap", pointerEvents: "none" }}>
              {photoBbox.component?.toUpperCase()}
            </div>
          </>
        )}
      </div>
      {photoBbox.comment && (
        <div style={{ padding: "8px 10px", fontSize: 8, color: "#9ca3af", lineHeight: 1.6, borderTop: `1px solid ${statusColor}20` }}>{photoBbox.comment}</div>
      )}
      <div style={{ padding: "0 10px 10px" }}>
        <ConfidenceBar confidence={photoBbox.confidence} status={photoBbox.status} />
      </div>
    </div>
  );
}

// ── KEYWORD TAGS ──────────────────────────────────────────────────────────
function KeywordTags({ terms }) {
  if (!terms || terms.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 5 }}>
      {terms.map((t, i) => {
        const c = CAT_COLORS[t.cat];
        return (
          <span key={i} style={{ fontSize: 7, fontWeight: 700, color: c.text, background: c.bg, border: `1px solid ${c.border}`, padding: "1px 4px", borderRadius: 2, letterSpacing: 0.5 }}>
            {t.short}
          </span>
        );
      })}
    </div>
  );
}

function MachineTag({ machine }) {
  if (!machine) return null;
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 5, padding: "3px 8px", borderRadius: 4, background: "#0d1117", border: "1px solid #FFCD11" }}>
      <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#FFCD11" }} />
      <span style={{ fontSize: 8, color: "#FFCD11", fontWeight: 600, letterSpacing: 1 }}>{machine.id}</span>
      <span style={{ fontSize: 7, color: "#6b7280" }}>{machine.model}</span>
    </div>
  );
}

// ── VOICE TAB ─────────────────────────────────────────────────────────────
function VoicePanel({ chunks, processing, fields, isRecording }) {
  const bottomRef = useRef(null);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chunks]);
  const outcome = !isRecording && !processing ? sessionOutcome(chunks, fields) : null;

  return (
    <div style={{ flex: 1, overflowY: "auto" }}>
      {processing && (
        <div style={{ padding: "8px 14px", display: "flex", gap: 4, alignItems: "center", borderBottom: "1px solid #141414" }}>
          {[0, 1, 2].map(i => <div key={i} style={{ width: 4, height: 4, borderRadius: "50%", background: "#FFCD11", animation: `blink 1s ${i * 0.25}s infinite` }} />)}
          <span style={{ fontSize: 8, color: "#FFCD11", marginLeft: 4, letterSpacing: 1 }}>ANALYZING</span>
        </div>
      )}
      {chunks.length === 0 ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60%", flexDirection: "column", gap: 6 }}>
          <div style={{ fontSize: 9, color: "#374151", textAlign: "center", lineHeight: 2 }}>press record below<br />start talking</div>
        </div>
      ) : chunks.map((chunk, i) => {
        const isLast = i === chunks.length - 1;
        const displaySummary = (chunk.summary && chunk.summary !== "null") ? chunk.summary : null;
        const isIssue = displaySummary?.startsWith("⚠");
        const isGood = displaySummary?.startsWith("✓");
        return (
          <div key={i} style={{ padding: "10px 14px", borderBottom: "1px solid #0d0d0d", borderLeft: isLast ? "2px solid #FFCD11" : "2px solid transparent", background: isLast ? "#0a0d12" : "transparent" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
              <span style={{ fontSize: 7, color: "#FFCD11", opacity: 0.5 }}>{chunk.time}</span>
              {isLast && isRecording && <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#ef4444", animation: "blink 1.5s infinite" }} />}
              {chunk.autoSmell && <span style={{ fontSize: 7, color: "#ef4444", background: "#200a0a", padding: "1px 4px", borderRadius: 2 }}>🔥 SMOKE DETECTED</span>}
            </div>
            <div style={{ fontSize: isLast ? 12 : 10, color: isLast ? "#e5e7eb" : "#9ca3af", lineHeight: 1.7 }}>{chunk.text}</div>
            <MachineTag machine={chunk.detectedMachine} />
            {displaySummary && (
              <div style={{ fontSize: 8, marginTop: 5, padding: "3px 7px", borderRadius: 10, display: "inline-block", background: isIssue ? "#200a0a" : isGood ? "#0a2016" : "#111827", color: isIssue ? "#f87171" : isGood ? "#4ade80" : "#6b7280", border: `1px solid ${isIssue ? "#7f1d1d" : isGood ? "#14532d" : "#1e2530"}`, letterSpacing: 0.3 }}>
                {displaySummary}
              </div>
            )}
            <KeywordTags terms={chunk.terms} />
          </div>
        );
      })}
      {outcome && (
        <div style={{ margin: "16px 14px", padding: "10px 14px", borderRadius: 6, background: outcome.bg, border: `1px solid ${outcome.color}`, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 24, height: 24, borderRadius: "50%", background: outcome.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#000", fontWeight: 700, flexShrink: 0 }}>{outcome.icon}</div>
          <div>
            <div style={{ fontSize: 7, color: outcome.color, letterSpacing: 2, marginBottom: 2 }}>SESSION OUTCOME</div>
            <div style={{ fontSize: 10, color: outcome.color, fontWeight: 600 }}>{outcome.label}</div>
          </div>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}

// ── REPORT TAB ────────────────────────────────────────────────────────────
function ReportPanel({ fields, photoBbox, activeMachine }) {
  const sections = fields.reduce((acc, f) => {
    if (!acc[f.section]) acc[f.section] = [];
    acc[f.section].push(f);
    return acc;
  }, {});

  return (
    <div style={{ flex: 1, overflowY: "auto" }}>
      {/* Machine info banner */}
      <div style={{ padding: "6px 14px", background: "#0d0d0d", borderBottom: "1px solid #141414", display: "flex", gap: 8, alignItems: "center" }}>
        <span style={{ fontSize: 7, color: "#FFCD11", letterSpacing: 1 }}>{activeMachine.id}</span>
        <span style={{ fontSize: 7, color: "#4b5563" }}>·</span>
        <span style={{ fontSize: 7, color: "#6b7280" }}>{activeMachine.fullName}</span>
        <span style={{ fontSize: 7, color: "#374151", marginLeft: "auto" }}>FORM: {activeMachine.inspectionForm}</span>
      </div>
      <PhotoBboxOverlay photoBbox={photoBbox} />
      {Object.entries(sections).map(([section, sfields]) => (
        <div key={section}>
          <div style={{ padding: "4px 14px", fontSize: 7, color: "#6b7280", letterSpacing: 2, borderBottom: "1px solid #141414", borderTop: "1px solid #141414", background: "#0d0d0d" }}>{section}</div>
          {sfields.map(field => {
            const lbl = LABEL[field.status];
            return (
              <div key={field.id} style={{ padding: "8px 14px", borderBottom: "1px solid #0d0d0d" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: DOT[field.status], flexShrink: 0, boxShadow: field.status ? `0 0 5px ${DOT[field.status]}` : "none", transition: "all 0.4s" }} />
                  <span style={{ fontSize: 9, color: "#9ca3af", flex: 1 }}>{field.label}</span>
                  <span style={{ fontSize: 6, color: "#374151" }}>{field.id}</span>
                  {field.status && lbl && (
                    <span style={{ fontSize: 7, color: lbl.color, background: lbl.bg, padding: "2px 6px", borderRadius: 2, fontWeight: 600 }}>{field.status}</span>
                  )}
                </div>
                {field.lookFor && !field.status && (
                  <div style={{ fontSize: 7, color: "#374151", marginLeft: 13, marginTop: 2 }}>check: {field.lookFor}</div>
                )}
                {field.comment && <div style={{ fontSize: 8, color: "#6b7280", marginLeft: 13, marginTop: 3, lineHeight: 1.5 }}>{field.comment}</div>}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ── FLEET / MACHINE MODELS TAB ────────────────────────────────────────────
function MachineModelsPanel({ localHistory, memoryLoading = false, smellLog = [], chunks = [], fields = [], onOpenChat = () => {} }) {
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [expandedInspection, setExpandedInspection] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("ALL");

  const categories = ["ALL", "LOADER", "EXCAVATOR", "TRUCK", "DOZER"];
  const filteredMachines = categoryFilter === "ALL" ? MACHINES : MACHINES.filter(m => m.category === categoryFilter);

  if (selectedMachine) {
    const machine = MACHINES.find(m => m.id === selectedMachine);
    const history = localHistory[selectedMachine] || [];
    const failCount = history.filter(m => parseMemory(m).fail !== "none").length;
    const passCount = history.filter(m => parseMemory(m).fail === "none" && parseMemory(m).monitor === "none").length;
    const fieldHealth = {};
    history.forEach(mem => {
      (mem.fields || []).forEach(f => {
        if (!fieldHealth[f.id]) fieldHealth[f.id] = { label: f.label, statuses: [] };
        fieldHealth[f.id].statuses.push(f.status);
      });
    });
    const clsColors = CAT_CLASS_COLORS[machine.category] || CAT_CLASS_COLORS.LOADER;

    // Current session summary for THIS machine
    const currentSummary = generateTranscriptSummary(chunks, fields);

    return (
      <div style={{ flex: 1, overflowY: "auto" }}>
        <div style={{ padding: "10px 14px", borderBottom: "1px solid #141414", display: "flex", alignItems: "center", gap: 10 }}>
          <div onClick={() => { setSelectedMachine(null); setExpandedInspection(null); }} style={{ fontSize: 12, color: "#FFCD11", cursor: "pointer" }}>◂</div>
          <span style={{ fontSize: 9, color: "#9ca3af", letterSpacing: 1 }}>FLEET</span>
          <span style={{ fontSize: 9, color: clsColors.text, marginLeft: "auto" }}>{machine.categoryIcon} {machine.id}</span>
        </div>

        {/* Machine detail card */}
        <div style={{ padding: "16px 14px", borderBottom: "1px solid #141414", background: "#0d0d0d" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <MachineSVG machine={machine} size={100} />
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                <span style={{ fontSize: 7, padding: "1px 5px", borderRadius: 2, background: clsColors.bg, color: clsColors.text, border: `1px solid ${clsColors.border}`, fontWeight: 700, letterSpacing: 1 }}>{machine.category}</span>
              </div>
              <div style={{ fontSize: 16, color: "#FFCD11", fontWeight: 600 }}>{machine.model}</div>
              <div style={{ fontSize: 8, color: "#6b7280", marginTop: 1 }}>{machine.fullName}</div>
              <div style={{ fontSize: 7, color: "#4b5563", marginTop: 3 }}>
                UNIT: <span style={{ color: "#FFCD11" }}>{machine.id}</span>
                <span style={{ color: "#1e2530" }}> · </span>
                ASSET: <span style={{ color: "#9ca3af" }}>{machine.assetId}</span>
              </div>
              <div style={{ fontSize: 7, color: "#4b5563" }}>S/N: {machine.serialNo} · {machine.year} · {machine.smuHours.toLocaleString()} SMU HRS</div>
              <div style={{ fontSize: 7, color: "#4b5563" }}>OPERATOR: {machine.operatorName}</div>
              <div style={{ fontSize: 7, color: "#4b5563", marginTop: 2 }}>FORM: <span style={{ color: "#6b7280" }}>{machine.inspectionForm}</span></div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <span style={{ fontSize: 7, padding: "2px 6px", borderRadius: 3, background: "#200a0a", color: "#ef4444", border: "1px solid #7f1d1d" }}>{failCount} INSP W/ FAIL</span>
            <span style={{ fontSize: 7, padding: "2px 6px", borderRadius: 3, background: "#0a2016", color: "#22c55e", border: "1px solid #14532d" }}>{passCount} CLEAN</span>
            {smellLog.filter(s => s.machine === machine.id).length > 0 && (
              <span style={{ fontSize: 7, padding: "2px 6px", borderRadius: 3, background: "#200a0a", color: "#fca5a5", border: "1px solid #7f1d1d" }}>🔥 {smellLog.filter(s => s.machine === machine.id).length} SMELL</span>
            )}
          </div>
        </div>

        {/* AI CHAT SHORTCUT in fleet */}
        <div style={{ padding: "8px 14px", borderBottom: "1px solid #141414" }}>
          <div style={{ fontSize: 7, color: "#4b5563", marginBottom: 6 }}>Ask AI about this machine's inspection data:</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {["Summarize inspection", "Any safety concerns?", "Maintenance needed?"].map((q, i) => (
              <div key={i} style={{ padding: "4px 8px", borderRadius: 4, border: "1px solid #1d4ed8", fontSize: 7, color: "#60a5fa", cursor: "pointer", background: "#0a1628" }}
                onClick={() => onOpenChat(q)}>
                {q}
              </div>
            ))}
          </div>
        </div>

        {/* CURRENT SESSION TRANSCRIPT SUMMARY */}
        {currentSummary && chunks.length > 0 && (
          <div style={{ padding: "10px 14px", borderBottom: "1px solid #141414", background: "#0a0d12" }}>
            <div style={{ fontSize: 7, color: "#FFCD11", letterSpacing: 2, marginBottom: 6 }}>CURRENT SESSION SUMMARY</div>
            <div style={{ fontSize: 8, color: "#9ca3af", lineHeight: 1.7 }}>{currentSummary}</div>
            <div style={{ fontSize: 7, color: "#374151", marginTop: 4 }}>{chunks.length} voice chunks · {chunks.map(c => c.text.split(" ").length).reduce((a, b) => a + b, 0)} words transcribed</div>
          </div>
        )}

        {/* Component health trend */}
        {Object.keys(fieldHealth).length > 0 && (
          <div style={{ padding: "10px 14px", borderBottom: "1px solid #141414" }}>
            <div style={{ fontSize: 7, color: "#6b7280", letterSpacing: 2, marginBottom: 8 }}>COMPONENT HEALTH TREND</div>
            {Object.entries(fieldHealth).map(([id, data]) => {
              const lastStatus = data.statuses[data.statuses.length - 1];
              const fCount = data.statuses.filter(s => s === "FAIL").length;
              return (
                <div key={id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: DOT[lastStatus] || "#1e2530", flexShrink: 0 }} />
                  <span style={{ fontSize: 8, color: "#9ca3af", flex: 1 }}>{data.label}</span>
                  <div style={{ display: "flex", gap: 2 }}>
                    {data.statuses.slice(-5).map((s, i) => (
                      <div key={i} style={{ width: 8, height: 8, borderRadius: 2, background: DOT[s] || "#1e2530", opacity: 0.8 }} />
                    ))}
                  </div>
                  {fCount > 0 && <span style={{ fontSize: 7, color: "#ef4444" }}>{fCount}/{data.statuses.length}F</span>}
                </div>
              );
            })}
          </div>
        )}

        {/* Smell alerts */}
        {smellLog.filter(s => s.machine === selectedMachine).length > 0 && (
          <div style={{ padding: "10px 14px", borderBottom: "1px solid #141414" }}>
            <div style={{ fontSize: 7, color: "#ef4444", letterSpacing: 2, marginBottom: 8 }}>SMELL ALERTS</div>
            {smellLog.filter(s => s.machine === selectedMachine).map((entry, i) => (
              <div key={i} style={{ marginBottom: 6, padding: "6px 8px", borderRadius: 4, background: "#200a0a", border: "1px solid #7f1d1d" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                  <span style={{ fontSize: 7, color: "#6b7280" }}>{entry.time}{entry.autoDetected ? " · 🎙 AUTO" : ""}</span>
                  <span style={{ fontSize: 7, color: "#ef4444" }}>INTENSITY {entry.intensity}%</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                  {entry.smells.map((s, j) => <span key={j} style={{ fontSize: 7, color: "#fca5a5" }}>{s.icon} {s.label}</span>)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Inspection log with summaries */}
        <div style={{ padding: "8px 14px 4px", fontSize: 7, color: "#6b7280", letterSpacing: 2 }}>INSPECTION LOG</div>
        {history.length === 0 ? (
          <div style={{ padding: "20px", textAlign: "center", fontSize: 9, color: "#374151" }}>no inspections yet</div>
        ) : [...history].reverse().map((mem, i) => {
          const p = parseMemory(mem);
          const hasFail = p.fail !== "none", hasMon = p.monitor !== "none";
          return (
            <div key={i} onClick={() => setExpandedInspection(expandedInspection === i ? null : i)}
              style={{ padding: "10px 14px", borderBottom: "1px solid #0d0d0d", cursor: "pointer", borderLeft: hasFail ? "2px solid #ef4444" : hasMon ? "2px solid #eab308" : "2px solid #22c55e" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 9, color: "#9ca3af" }}>{mem.date}</div>
                <div style={{ display: "flex", gap: 4 }}>
                  {hasFail && <span style={{ fontSize: 7, color: "#ef4444", background: "#200a0a", padding: "1px 5px", borderRadius: 2 }}>FAIL</span>}
                  {hasMon && <span style={{ fontSize: 7, color: "#eab308", background: "#1f1500", padding: "1px 5px", borderRadius: 2 }}>MON</span>}
                  {!hasFail && !hasMon && <span style={{ fontSize: 7, color: "#22c55e", background: "#0a2016", padding: "1px 5px", borderRadius: 2 }}>PASS</span>}
                </div>
              </div>
              {/* Transcript summary shown per inspection */}
              {p.summary && <div style={{ fontSize: 7, color: "#6b7280", marginTop: 3, fontStyle: "italic" }}>"{p.summary}"</div>}
              {hasFail && <div style={{ fontSize: 8, color: "#ef4444", marginTop: 3 }}>✗ {p.fail}</div>}
              {hasMon && <div style={{ fontSize: 8, color: "#eab308", marginTop: 2 }}>⚠ {p.monitor}</div>}
              {expandedInspection === i && (
                <div style={{ marginTop: 8, padding: 10, background: "#0d0d0d", borderRadius: 4, fontSize: 8, color: "#6b7280", lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{p.full}</div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Fleet grid view
  return (
    <div style={{ flex: 1, overflowY: "auto" }}>
      {/* Category filter tabs */}
      <div style={{ padding: "8px 14px", borderBottom: "1px solid #141414", display: "flex", gap: 4, flexWrap: "wrap" }}>
        {categories.map(cat => {
          const clr = CAT_CLASS_COLORS[cat] || { border: "#374151", text: "#4b5563", bg: "transparent" };
          const count = cat === "ALL" ? MACHINES.length : MACHINES.filter(m => m.category === cat).length;
          return (
            <button key={cat} onClick={() => setCategoryFilter(cat)}
              style={{ padding: "3px 8px", borderRadius: 3, fontSize: 7, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", letterSpacing: 0.5, border: "1px solid", background: categoryFilter === cat ? clr.bg : "transparent", color: categoryFilter === cat ? clr.text : "#4b5563", borderColor: categoryFilter === cat ? clr.border : "#1e2530", transition: "all 0.2s" }}>
              {cat} {count > 0 ? `(${count})` : ""}
            </button>
          );
        })}
        {memoryLoading && (
          <span style={{ fontSize: 7, color: "#FFCD11", display: "flex", gap: 3, alignItems: "center", marginLeft: "auto" }}>
            {[0, 1, 2].map(i => <span key={i} style={{ width: 3, height: 3, borderRadius: "50%", background: "#FFCD11", display: "inline-block", animation: `blink 1s ${i * 0.2}s infinite` }} />)}
            <span style={{ marginLeft: 3 }}>SYNCING</span>
          </span>
        )}
      </div>

      {filteredMachines.map(machine => {
        const history = localHistory[machine.id] || [];
        const lastInsp = history.length > 0 ? history[history.length - 1] : null;
        const lastP = lastInsp ? parseMemory(lastInsp) : null;
        const hasFail = lastP ? lastP.fail !== "none" : false;
        const hasMon = lastP ? lastP.monitor !== "none" : false;
        const statusColor = hasFail ? "#ef4444" : hasMon ? "#eab308" : history.length > 0 ? "#22c55e" : "#374151";
        const clsColors = CAT_CLASS_COLORS[machine.category] || CAT_CLASS_COLORS.LOADER;
        return (
          <div key={machine.id} onClick={() => setSelectedMachine(machine.id)}
            style={{ padding: "14px", margin: "10px 14px", borderRadius: 8, background: "#0d0d0d", border: "1px solid #1e2530", cursor: "pointer", transition: "all 0.2s" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ flexShrink: 0, opacity: history.length > 0 ? 1 : 0.4 }}>
                <MachineSVG machine={machine} size={65} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <span style={{ fontSize: 7, padding: "1px 4px", borderRadius: 2, background: clsColors.bg, color: clsColors.text, border: `1px solid ${clsColors.border}`, fontWeight: 700 }}>{machine.categoryIcon} {machine.category}</span>
                    </div>
                    <div style={{ fontSize: 11, color: "#FFCD11", fontWeight: 600, marginTop: 2 }}>{machine.id}</div>
                    <div style={{ fontSize: 8, color: "#6b7280" }}>{machine.model} · {machine.year}</div>
                    <div style={{ fontSize: 7, color: "#374151" }}>ASSET: {machine.assetId}</div>
                  </div>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: statusColor, marginTop: 3, boxShadow: history.length > 0 ? `0 0 6px ${statusColor}` : "none" }} />
                </div>
                <div style={{ marginTop: 5, fontSize: 7, color: "#4b5563", display: "flex", flexWrap: "wrap", gap: 6 }}>
                  <span>{history.length} inspections{lastInsp ? ` · last: ${lastInsp.date?.split(" ")[0]}` : ""}</span>
                  {smellLog.filter(s => s.machine === machine.id).length > 0 && (
                    <span style={{ color: "#ef4444" }}>🔥 {smellLog.filter(s => s.machine === machine.id).length} smells</span>
                  )}
                  <span style={{ color: "#374151" }}>{machine.smuHours.toLocaleString()} hrs</span>
                </div>
                {/* Last inspection summary */}
                {lastP?.summary && <div style={{ fontSize: 7, color: "#6b7280", marginTop: 3, fontStyle: "italic" }}>"{lastP.summary.substring(0, 55)}..."</div>}
                {hasFail && <div style={{ fontSize: 7, color: "#ef4444", marginTop: 3 }}>✗ {lastP.fail.substring(0, 45)}{lastP.fail.length > 45 ? "..." : ""}</div>}
                {!hasFail && hasMon && <div style={{ fontSize: 7, color: "#eab308", marginTop: 3 }}>⚠ {lastP.monitor.substring(0, 45)}</div>}
                {!hasFail && !hasMon && history.length > 0 && <div style={{ fontSize: 7, color: "#22c55e", marginTop: 3 }}>✓ Last inspection passed</div>}
                {history.length === 0 && <div style={{ fontSize: 7, color: "#374151", marginTop: 3 }}>No inspections recorded</div>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── LIVE WAVEFORM ─────────────────────────────────────────────────────────
function LiveWaveform({ recording, processing }) {
  const bars = 32;
  const phaseRef   = React.useRef(0);
  const freqRef    = React.useRef(new Uint8Array(bars));
  const analyserRef = React.useRef(null);
  const streamRef  = React.useRef(null);
  const audioCtxRef = React.useRef(null);
  const rafRef     = React.useRef(null);
  const [heights, setHeights] = React.useState(Array(bars).fill(2));

  // Start mic capture when recording begins
  React.useEffect(() => {
    if (recording) {
      navigator.mediaDevices.getUserMedia({ audio: true, video: false })
        .then(stream => {
          streamRef.current = stream;
          const AudioCtx = window.AudioContext || window.webkitAudioContext;
          const ctx = new AudioCtx();
          audioCtxRef.current = ctx;
          const source = ctx.createMediaStreamSource(stream);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 128;
          analyser.smoothingTimeConstant = 0.75;
          source.connect(analyser);
          analyserRef.current = analyser;
          freqRef.current = new Uint8Array(analyser.frequencyBinCount);

          const draw = () => {
            analyser.getByteFrequencyData(freqRef.current);
            const data = freqRef.current;
            setHeights(Array.from({ length: bars }, (_, i) => {
              // Map bar index across frequency bins
              const binIdx = Math.floor((i / bars) * data.length);
              const vol = data[binIdx] / 255;
              // Add gentle sine wave shape so silent moments still look alive
              phaseRef.current += 0.015;
              const wave = Math.abs(Math.sin(i * 0.4 + phaseRef.current)) * 0.15;
              return 2 + (vol + wave) * 14;
            }));
            rafRef.current = requestAnimationFrame(draw);
          };
          rafRef.current = requestAnimationFrame(draw);
        })
        .catch(() => {
          // Mic denied — fall back to animated sine wave
          const draw = () => {
            phaseRef.current += 0.06;
            setHeights(Array.from({ length: bars }, (_, i) => {
              const b = Math.abs(Math.sin(i * 0.5 + phaseRef.current * 2));
              return 2 + b * 11;
            }));
            rafRef.current = requestAnimationFrame(draw);
          };
          rafRef.current = requestAnimationFrame(draw);
        });
    } else {
      // Stop mic + analyser
      cancelAnimationFrame(rafRef.current);
      analyserRef.current = null;
      streamRef.current?.getTracks().forEach(t => t.stop());
      audioCtxRef.current?.close().catch(() => {});
      streamRef.current = null;
      audioCtxRef.current = null;

      if (processing) {
        // Processing: medium sine pulse
        const draw = () => {
          phaseRef.current += 0.05;
          setHeights(Array.from({ length: bars }, (_, i) => {
            const b = Math.abs(Math.sin(i * 0.6 + phaseRef.current * 1.8));
            return 2 + b * 7;
          }));
          rafRef.current = requestAnimationFrame(draw);
        };
        rafRef.current = requestAnimationFrame(draw);
      } else {
        // Idle: very slow gentle breathe
        const draw = () => {
          phaseRef.current += 0.008;
          setHeights(Array.from({ length: bars }, (_, i) => {
            const b = Math.abs(Math.sin(i * 0.3 + phaseRef.current));
            return 2 + b * 1.5;
          }));
          rafRef.current = requestAnimationFrame(draw);
        };
        rafRef.current = requestAnimationFrame(draw);
      }
    }
    return () => {
      cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach(t => t.stop());
      audioCtxRef.current?.close().catch(() => {});
    };
  }, [recording, processing]);

  const color = recording ? "#ef4444" : processing ? "#FFCD11" : "#2a2a2a";
  return (
    <div style={{ display: "flex", justifyContent: "center", gap: 2, alignItems: "center", height: 14, marginBottom: 6 }}>
      {heights.map((h, i) => (
        <div key={i} style={{
          width: 2, borderRadius: 1,
          background: (recording || processing)
            ? `linear-gradient(to top, ${color}60, ${color})`
            : "#1e2530",
          height: `${Math.max(2, h)}px`,
          transition: recording ? "none" : "height 0.15s ease, background 0.4s",
          boxShadow: recording && h > 8 ? `0 0 4px ${color}70` : "none",
        }} />
      ))}
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────────────
export default function App() {
  const { recording, audioBlob, start, stop } = useAudioRecorder();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [fields, setFields] = useState(DEFAULT_FIELDS);
  const [chunks, setChunks] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [photoStatus, setPhotoStatus] = useState(null);
  const [exported, setExported] = useState(false);
  const [tab, setTab] = useState("voice");
  const [localHistory, setLocalHistory] = useState({});
  const [activeMachine, setActiveMachine] = useState(MACHINES[0]);
  const [photoBbox, setPhotoBbox] = useState(null);
  const [memoryLoading, setMemoryLoading] = useState(false);
  const [smellLog, setSmellLog] = useState([]);
  const [lastAutoDetect, setLastAutoDetect] = useState(null);
  const [showWordbank, setShowWordbank] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [voiceTabHint, setVoiceTabHint] = useState(null);
  const [supermemoryStatus, setSupermemoryStatus] = useState("unknown"); // "ok" | "fail" | "unknown"

  // ── Switch machine → update field template ────────────────────────────
  const switchMachine = (machine) => {
    setActiveMachine(machine);
    setFields(getFieldsForMachine(machine));
    setChunks([]);
    setPhotoBbox(null);
    setSmellLog([]);
  };

  // ── Intro animation ──────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setShowIntro(false), 2200);
    return () => clearTimeout(t);
  }, []);

  // ── Load from Supermemory on mount + verify connection ─────────────────
  useEffect(() => {
    (async () => {
      setMemoryLoading(true);
      try {
        const allHistory = {};
        await Promise.all(MACHINES.map(async (m) => {
          try {
            const records = await loadMachineHistory(m.id);
            if (records && records.length > 0) {
              allHistory[m.id] = records.map(r => ({
                date: r.metadata?.date || r.date || "Unknown",
                machineId: m.id,
                fields: r.metadata?.fields || [],
                content: r.content,
              }));
            }
          } catch { /* individual machine fail is ok */ }
        }));
        if (Object.keys(allHistory).length > 0) setLocalHistory(allHistory);
        setSupermemoryStatus("ok");
      } catch (e) {
        console.warn("Supermemory load failed:", e);
        setSupermemoryStatus("fail");
      } finally {
        setMemoryLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Recording timer ───────────────────────────────────────────────────
  useEffect(() => {
    let t;
    if (recording) t = setInterval(() => setElapsed(e => e + 1), 1000);
    else setElapsed(0);
    return () => clearInterval(t);
  }, [recording]);

  // ── Audio processing ──────────────────────────────────────────────────
  useEffect(() => {
    if (!audioBlob) return;
    (async () => {
      setProcessing(true);
      try {
        const text = await transcribeAudio(audioBlob);
        if (!text?.trim()) return;
        const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        const terms = extractCATTerms(text);
        const detectedMachine = detectMachineFromText(text);
        // NOTE: We detect machine for display/tagging only — do NOT auto-switch
        // Auto-switching caused cross-machine field/smell contamination (EX→FL bug)

        // ── VOICE TAB COMMANDS ─────────────────────────────────────────
        const lower = text.toLowerCase();
        if (/go to report|open report|show report|switch to report/.test(lower)) {
          setTab("report"); setVoiceTabHint("REPORT");
          setTimeout(() => setVoiceTabHint(null), 2000);
        } else if (/go to smell|open smell|smell tab|show smell/.test(lower)) {
          setTab("smell"); setVoiceTabHint("SMELL");
          setTimeout(() => setVoiceTabHint(null), 2000);
        } else if (/go to fleet|open fleet|show fleet|fleet tab/.test(lower)) {
          setTab("machines"); setVoiceTabHint("FLEET");
          setTimeout(() => setVoiceTabHint(null), 2000);
        } else if (/go to voice|open voice|voice tab|show voice/.test(lower)) {
          setTab("voice"); setVoiceTabHint("VOICE");
          setTimeout(() => setVoiceTabHint(null), 2000);
        }

        // ── REAL-TIME SMOKE DETECTION from transcript ──────────────────
        const autoSmells = detectSmokeFromText(text);
        let autoSmellFlag = false;
        if (autoSmells.length > 0) {
          autoSmellFlag = true;
          const entry = { time, smells: autoSmells, intensity: 70, machine: activeMachine.id, autoDetected: true };
          setSmellLog(prev => [...prev, entry]);
          setLastAutoDetect([...autoSmells.map(s => ({ ...s, time }))]);
          // Auto-fill forms based on detected smells
          if (autoSmells.some(s => ["blue_smoke", "white_smoke", "sweet"].includes(s.id))) {
            setFields(prev => prev.map(f => {
              const coolantId = f.id === "2.2" || f.id === "E.1";
              return coolantId && !f.status ? { ...f, status: "FAIL", comment: `Auto-detected: ${autoSmells.map(s => s.label).join(", ")}` } : f;
            }));
          }
        }

        let summary;
        try { summary = await summarizeChunk(text); } catch { summary = quickSummary(text); }
        setChunks(prev => [...prev, { time, text: text.trim(), terms, summary, detectedMachine, autoSmell: autoSmellFlag }]);
        appendToContext(text, time);

        const result = await fillInspectionForm();
        if (result?.fields) {
          setFields(prev => prev.map(f => {
            const u = result.fields.find(r => r.id === f.id);
            return u ? { ...f, ...u } : f;
          }));
        }
      } finally { setProcessing(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioBlob]);

  // ── LOGIN GATE — after ALL hooks ──────────────────────────────────────
  if (!isAuthenticated) {
    return <CopyCatLogin onLoginSuccess={(user) => { setCurrentUser(user); setIsAuthenticated(true); }} />;
  }

  // ── Photo handler ─────────────────────────────────────────────────────
  const handlePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoStatus("analyzing");
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const base64 = reader.result.split(",")[1];
        const [result, bbox] = await Promise.all([analyzePhoto(base64), detectBoundingBox(base64)]);
        if (bbox) {
          setPhotoBbox({ ...bbox, imageBase64: base64, status: result?.status, comment: result?.comment, confidence: result?.confidence });
          setTab("report");
        }
        if (result?.field_id) {
          setFields(prev => prev.map(f => f.id === result.field_id ? { ...f, status: result.status, comment: result.comment } : f));
        }
        if (result?.comment) speakFinding(`${result.status}. ${result.comment}`);
        setPhotoStatus("done");
        setTimeout(() => setPhotoStatus(null), 2000);
      } catch { setPhotoStatus(null); }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  // ── Smell handler ─────────────────────────────────────────────────────
  const handleSmellDetected = (smells, intensity) => {
    const time = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    setSmellLog(prev => [...prev, { time, smells, intensity, machine: activeMachine.id }]);
    const failSmells = smells.filter(s => s.status === "FAIL");
    if (failSmells.some(s => ["blue_smoke", "white_smoke", "sweet"].includes(s.id))) {
      setFields(prev => prev.map(f => {
        const isCoolant = f.id === "2.2" || f.id === "E.1";
        return isCoolant && !f.status ? { ...f, status: "FAIL", comment: `Smell: ${failSmells.map(s => s.label).join(", ")}` } : f;
      }));
    }
    if (failSmells.some(s => ["burning", "hot_metal", "rubber"].includes(s.id))) {
      setFields(prev => prev.map(f => {
        const isTrans = f.id === "1.7" || f.id === "G.7";
        return isTrans && !f.status ? { ...f, status: "MONITOR", comment: `Thermal smell: ${failSmells.map(s => s.label).join(", ")}` } : f;
      }));
    }
    const smellNames = smells.map(s => s.label).join(", ");
    speakFinding(`Smell alert. Detected ${smellNames} at ${intensity} percent intensity on ${activeMachine.id}.`);
  };

  // ── Export ────────────────────────────────────────────────────────────
  const handleExport = () => {
    const transcript = chunks.map(c => `[${c.time}] ${c.text}`);
    const transcriptSummary = generateTranscriptSummary(chunks, fields);
    const record = {
      date: new Date().toLocaleDateString() + " " + new Date().toLocaleTimeString(),
      machineId: activeMachine.id,
      fields: fields.filter(f => f.status),
      content: `INSPECTION — ${activeMachine.id} (${activeMachine.fullName}) — ${new Date().toLocaleDateString()}
ASSET ID: ${activeMachine.assetId} · SERIAL: ${activeMachine.serialNo} · FORM: ${activeMachine.inspectionForm}
OPERATOR: ${activeMachine.operatorName} · SMU: ${activeMachine.smuHours} hrs

SUMMARY: ${transcriptSummary || "No issues detected"}

PASS: ${fields.filter(f => f.status === "PASS").map(f => f.label).join(", ") || "none"}
MONITOR: ${fields.filter(f => f.status === "MONITOR").map(f => `${f.label}: ${f.comment}`).join(", ") || "none"}
FAIL: ${fields.filter(f => f.status === "FAIL").map(f => `${f.label}: ${f.comment}`).join(", ") || "none"}

SMELL ALERTS:
${smellLog.length > 0 ? smellLog.map(e => `[${e.time}]${e.autoDetected ? " AUTO" : ""} ${e.smells.map(s => s.label).join(", ")} — intensity ${e.intensity}%`).join("\n") : "none"}

TRANSCRIPT:
${transcript.join("\n")}`,
    };

    const blob = new Blob([record.content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `inspection-${activeMachine.id}-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);

    setLocalHistory(prev => ({ ...prev, [activeMachine.id]: [...(prev[activeMachine.id] || []), record] }));
    saveInspection(activeMachine.id, record).catch(e => console.warn("Supermemory save failed:", e));
    setExported(true);
    setTimeout(() => setExported(false), 2500);
  };

  const counts = {
    pass: fields.filter(f => f.status === "PASS").length,
    monitor: fields.filter(f => f.status === "MONITOR").length,
    fail: fields.filter(f => f.status === "FAIL").length,
  };

  return (
    <div style={{ background: "#0a0a0a", height: "100vh", color: "#e5e7eb", fontFamily: "'IBM Plex Mono', monospace", maxWidth: 430, margin: "0 auto", display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 2px; }
        ::-webkit-scrollbar-thumb { background: #1e2530; }
        @keyframes blink { 50% { opacity: 0; } }
        @keyframes fadein { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:none; } }
        @keyframes pulse { 0%,100% { transform:scale(1); } 50% { transform:scale(1.06); } }
        @keyframes waveAnim { 0%,100%{transform:scaleY(0.3)} 50%{transform:scaleY(1)} }
        select option { background: #0a0a0a; }
        button { transition: transform 0.15s ease, opacity 0.15s ease, background 0.2s ease, box-shadow 0.2s ease !important; }
        button:active { transform: scale(0.94) !important; }
        button:not(:disabled):hover { opacity: 0.88; }
      `}</style>

      {/* Wordbank modal */}
      {showWordbank && <WordbankModal onClose={() => setShowWordbank(false)} />}

      {/* INTRO ANIMATION OVERLAY */}
      {showIntro && (
        <div style={{ position: "absolute", inset: 0, background: "#0a0a0a", zIndex: 998, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", animation: "introOut 0.5s ease 1.8s forwards" }}>
          <style>{`@keyframes introOut { to { opacity: 0; pointer-events: none; } } @keyframes introIn { from { opacity:0; transform:scale(0.85) translateY(10px); } to { opacity:1; transform:none; } } @keyframes introPulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.08)} }`}</style>
          <div style={{ animation: "introIn 0.5s cubic-bezier(0.22,1,0.36,1)", textAlign: "center" }}>
            <div style={{ width: 64, height: 64, background: "#FFCD11", borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", boxShadow: "0 0 40px #FFCD1160", animation: "introPulse 1s ease infinite" }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#fff", letterSpacing: -1 }}>COPY<span style={{ color: "#FFCD11" }}>CAT</span></div>
            <div style={{ fontSize: 8, color: "#4b5563", letterSpacing: 3, marginTop: 4 }}>FIELD INSPECTION SYSTEM</div>
            <div style={{ marginTop: 20, display: "flex", gap: 4, justifyContent: "center" }}>
              {[0,1,2].map(i => <div key={i} style={{ width: 4, height: 4, borderRadius: "50%", background: "#FFCD11", animation: `blink 1s ${i*0.2}s infinite` }} />)}
            </div>
          </div>
        </div>
      )}

      {/* VOICE TAB SWITCH HINT */}
      {voiceTabHint && (
        <div style={{ position: "absolute", top: "45%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 997, background: "#FFCD11", color: "#000", padding: "10px 20px", borderRadius: 8, fontSize: 12, fontWeight: 900, letterSpacing: 2, animation: "fadein 0.2s ease", boxShadow: "0 4px 20px #FFCD1140" }}>
          🎙 → {voiceTabHint}
        </div>
      )}

      {/* AI CHATBOT — full component */}
      {chatOpen && (
        <CopyCatChat
          machine={activeMachine}
          fields={fields}
          smellLog={smellLog}
          chunks={chunks}
          onClose={() => setChatOpen(false)}
        />
      )}

      {/* TOP BAR */}
      <div style={{ padding: "6px 12px 5px", borderBottom: "1px solid #141414", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#fff", letterSpacing: 1 }}>COPYCAT <span style={{ color: "#FFCD11" }}>▸</span></div>
          <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
            <span style={{ fontSize: 7, padding: "1px 4px", borderRadius: 2, background: CAT_CLASS_COLORS[activeMachine.category]?.bg || "#0d1117", color: CAT_CLASS_COLORS[activeMachine.category]?.text || "#FFCD11", border: `1px solid ${CAT_CLASS_COLORS[activeMachine.category]?.border || "#FFCD11"}`, fontWeight: 700 }}>
              {activeMachine.categoryIcon} {activeMachine.category}
            </span>
            <span style={{ fontSize: 9, color: "#FFCD11", letterSpacing: 1 }}>{activeMachine.id}</span>
            <span style={{ fontSize: 8, color: "#4b5563" }}>· {activeMachine.model}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* Supermemory status dot */}
          <div title={`Supermemory: ${supermemoryStatus}`} style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: supermemoryStatus === "ok" ? "#22c55e" : supermemoryStatus === "fail" ? "#ef4444" : "#374151", boxShadow: supermemoryStatus === "ok" ? "0 0 5px #22c55e" : "none" }} />
            <span style={{ fontSize: 6, color: "#374151" }}>MEM</span>
          </div>
          {/* AI Chat button */}
          <button onClick={() => setChatOpen(true)}
            style={{ background: "none", border: "1px solid #1d4ed8", color: "#60a5fa", padding: "2px 7px", borderRadius: 3, cursor: "pointer", fontSize: 7, fontFamily: "inherit", letterSpacing: 1 }}>
            AI CHAT
          </button>
          {/* Wordbank button */}
          <button onClick={() => setShowWordbank(true)}
            style={{ background: "none", border: "1px solid #1e2530", color: "#6b7280", padding: "2px 7px", borderRadius: 3, cursor: "pointer", fontSize: 7, fontFamily: "inherit", letterSpacing: 1 }}>
            WORDBANK
          </button>
          <div style={{ textAlign: "right", fontSize: 9, lineHeight: 1.9 }}>
            <div style={{ color: counts.fail > 0 ? "#ef4444" : "#374151" }}>{counts.fail}F</div>
            <div style={{ color: counts.monitor > 0 ? "#eab308" : "#374151" }}>{counts.monitor}M</div>
            <div style={{ color: counts.pass > 0 ? "#22c55e" : "#374151" }}>{counts.pass}P</div>
          </div>
        </div>
      </div>

      {/* MACHINE SWITCHER */}
      <div style={{ padding: "4px 10px", borderBottom: "1px solid #141414", display: "flex", gap: 3, flexShrink: 0, overflowX: "auto" }}>
        {MACHINES.map(m => {
          const isActive = m.id === activeMachine.id;
          const clr = CAT_CLASS_COLORS[m.category] || CAT_CLASS_COLORS.LOADER;
          return (
            <button key={m.id} onClick={() => switchMachine(m)}
              style={{ padding: "3px 8px", borderRadius: 3, fontSize: 7, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", letterSpacing: 0.5, border: "1px solid", whiteSpace: "nowrap", background: isActive ? clr.bg : "transparent", color: isActive ? clr.text : "#4b5563", borderColor: isActive ? clr.border : "#1e2530", transition: "all 0.2s", flexShrink: 0 }}>
              {m.categoryIcon} {m.id}
            </button>
          );
        })}
      </div>

      {/* 4 TABS */}
      <div style={{ display: "flex", borderBottom: "1px solid #141414", flexShrink: 0 }}>
        {[["voice", "VOICE"], ["report", "REPORT"], ["smell", "SMELL"], ["machines", "FLEET"]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{ flex: 1, padding: "7px 0", background: "transparent", border: "none", borderBottom: tab === key ? `2px solid ${key === "smell" ? "#ef4444" : "#FFCD11"}` : "2px solid transparent", color: tab === key ? (key === "smell" ? "#ef4444" : "#FFCD11") : "#4b5563", fontSize: 7, letterSpacing: 0.5, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s", position: "relative" }}>
            {label}
            {key === "smell" && smellLog.length > 0 && (
              <span style={{ position: "absolute", top: 4, right: "20%", width: 5, height: 5, borderRadius: "50%", background: "#ef4444", display: "inline-block" }} />
            )}
          </button>
        ))}
      </div>

      {/* CONTENT */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {tab === "voice" && <VoicePanel chunks={chunks} processing={processing} fields={fields} isRecording={recording} />}
        {tab === "report" && <ReportPanel fields={fields} photoBbox={photoBbox} activeMachine={activeMachine} />}
        {tab === "smell" && <SmellPanel onSmellDetected={handleSmellDetected} smellLog={smellLog} lastAutoDetect={lastAutoDetect} />}
        {tab === "machines" && <MachineModelsPanel localHistory={localHistory} memoryLoading={memoryLoading} smellLog={smellLog} chunks={chunks} fields={fields} onOpenChat={(q) => { setChatOpen(true); }} />}
      </div>

      {/* BOTTOM CONTROLS — shown on all tabs */}
        <div style={{ borderTop: "1px solid #141414", padding: "6px 14px 8px", flexShrink: 0, background: "#0a0a0a" }}>
          {/* VOICE TAB COMMANDS HINT */}
          <div style={{ textAlign: "center", fontSize: 6, color: "#1e2530", marginBottom: 3, letterSpacing: 1 }}>
            say "go to fleet/report/smell/voice" to switch tabs
          </div>
          <LiveWaveform recording={recording} processing={processing} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginBottom: 8 }}>
            <div style={{ textAlign: "right", minWidth: 68 }}>
              <div style={{ fontSize: 8, letterSpacing: 1, color: processing ? "#FFCD11" : recording ? "#ef4444" : "#374151" }}>
                {processing ? "ANALYZING" : photoStatus === "analyzing" ? "SCANNING" : photoStatus === "done" ? "PHOTO ✓" : recording ? "● REC" : "READY"}
              </div>
              <div style={{ fontSize: 11, color: recording ? "#ef4444" : "#374151", fontWeight: 500, marginTop: 2 }}>{fmt(elapsed)}</div>
            </div>
            <button onClick={recording ? stop : start} style={{ width: 52, height: 52, borderRadius: "50%", border: `2px solid ${recording ? "#ef4444" : "#2d2d2d"}`, background: recording ? "#ef4444" : "#141414", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.2s", boxShadow: recording ? "0 0 18px rgba(239,68,68,0.45)" : "none", animation: recording ? "pulse 2s infinite" : "none" }}>
              {recording ? <div style={{ width: 16, height: 16, background: "#fff", borderRadius: 3 }} /> : <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#ef4444" }} />}
            </button>
            <label style={{ width: 44, height: 44, borderRadius: "50%", border: `1.5px solid ${photoStatus === "done" ? "#22c55e" : photoStatus === "analyzing" ? "#FFCD11" : "#2d2d2d"}`, background: "#141414", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, cursor: "pointer", transition: "all 0.2s" }}>
              <span style={{ fontSize: 18 }}>{photoStatus === "analyzing" ? "⏳" : photoStatus === "done" ? "✓" : "📷"}</span>
              <input type="file" accept="image/*" capture="environment" onChange={handlePhoto} style={{ display: "none" }} />
            </label>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => { setFields(getFieldsForMachine(activeMachine)); setChunks([]); setElapsed(0); setPhotoBbox(null); setSmellLog([]); setLastAutoDetect(null); }}
              style={{ flex: 1, padding: "7px 0", background: "transparent", border: "1px solid #1e2530", color: "#4b5563", borderRadius: 4, fontSize: 9, letterSpacing: 2, cursor: "pointer", fontFamily: "inherit" }}>RESET</button>
            <button onClick={handleExport}
              style={{ flex: 3, padding: "7px 0", background: exported ? "#22c55e" : "#FFCD11", border: "none", color: "#000", borderRadius: 4, fontSize: 10, fontWeight: 600, letterSpacing: 2, cursor: "pointer", fontFamily: "inherit", transition: "background 0.3s" }}>
              {exported ? "SAVED ✓" : `SAVE ${activeMachine.id} →`}
            </button>
          </div>
        </div>
    </div>
  );
}